import { Card, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Separator } from '../../components/ui/separator';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { ShoppingCart, FileText, AlertTriangle, Pause, Receipt, X, ChevronDown, ChevronUp, Zap, ShieldAlert, Apple } from 'lucide-react';
import { POSCartItem } from './POSCartItem';
import { CounselingSlipActions } from './CounselingSlipActions';
import { fmtCurrency } from '../../lib/localeUtils';
import { useState, useRef } from 'react';
import { POSInteractionReport } from './POSInteractionReport';

const SEVERITY_STYLE = {
  high:     'border-rose-400 bg-rose-50 text-rose-900 dark:bg-rose-950/30 dark:text-rose-300',
  moderate: 'border-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-300',
  unknown:  'border-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-300',
  info:     'border-sky-300 bg-sky-50 text-sky-900 dark:bg-sky-950/30 dark:text-sky-300',
};

const FOOD_SEVERITY_STYLE = {
  critical: 'border-rose-400 bg-rose-50 text-rose-900 dark:bg-rose-950/30 dark:text-rose-300',
  warning:  'border-emerald-400 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300',
  info:     'border-emerald-300 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300',
};

// ── Allergy Alert Banner (always visible, red, non-dismissible) ───────────────
const AllergyBanner = ({ alerts }) => {
  const [expanded, setExpanded] = useState(false);
  if (!alerts?.length) return null;

  const top = alerts[0];
  const rest = alerts.slice(1);
  const pair = (top.drug_pair || []).join(' → ');

  return (
    <div className="mx-4 mt-3 rounded-xl border-2 border-rose-500 bg-rose-50 px-3 py-2.5 space-y-1.5 dark:bg-rose-950/40 dark:text-rose-200"
      data-testid="pos-allergy-banner">
      <div className="flex items-center gap-1.5 font-bold text-xs text-rose-800 dark:text-rose-300">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        ALLERGY ALERT{alerts.length > 1 ? ` (${alerts.length})` : ''}
      </div>
      {pair && <p className="text-[11px] font-semibold text-rose-900 dark:text-rose-200">{pair}</p>}
      <p className="text-[11px] text-rose-800 dark:text-rose-300 leading-snug">{top.description}</p>
      <p className="text-[10px] text-rose-600 dark:text-rose-400 opacity-80">Source: {top.source} — Verify before dispensing</p>
      {rest.length > 0 && (
        <>
          <button type="button" onClick={() => setExpanded(v => !v)}
            className="flex items-center gap-1 text-[10px] font-semibold text-rose-700 hover:text-rose-900"
            data-testid="pos-allergy-expand">
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? 'Show less' : `+${rest.length} more`}
          </button>
          {expanded && rest.map((a) => (
            <div key={a.id} className="border-t border-rose-300 pt-1.5 space-y-0.5">
              <p className="text-[11px] font-semibold text-rose-900 dark:text-rose-200">{(a.drug_pair || []).join(' → ')}</p>
              <p className="text-[11px] text-rose-800 dark:text-rose-300 line-clamp-2">{a.description}</p>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

// ── Interaction Banner (dismissible, amber/sky) ───────────────────────────────
export const InteractionBanner = ({ alerts, dismissed, onDismiss, testIdPrefix = 'pos' }) => {
  const [expanded, setExpanded] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const triggerRef = useRef(null);
  if (!alerts?.length || dismissed) return null;

  const topAlert = alerts[0];
  const rest = alerts.slice(1);
  const style = SEVERITY_STYLE[topAlert.severity] || SEVERITY_STYLE.unknown;
  const pairLabel = (topAlert.drug_pair || []).join(' + ');

  return (
    <>
    <div className={`relative mt-3 rounded-xl border px-3 py-2.5 space-y-1.5 ${style}`} data-testid={`${testIdPrefix}-interaction-banner`}>
      <button ref={triggerRef} type="button" onClick={() => setReportOpen(true)} className="w-full pr-4 text-left space-y-1.5 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4" aria-label="View interaction report" data-testid={`${testIdPrefix}-interaction-report-open`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 font-semibold text-xs">
          <Zap className="h-3.5 w-3.5 shrink-0" />
          Interaction{alerts.length > 1 ? `s (${alerts.length})` : ''} detected
          {topAlert.severity && topAlert.severity !== 'info' && (
            <span className="ml-1 capitalize text-[10px] font-bold opacity-80">[{topAlert.severity}]</span>
          )}
        </div>
      </div>
      {pairLabel && <p className="text-[11px] font-medium">{pairLabel}</p>}
      <p className="text-[11px] opacity-90 leading-snug line-clamp-2">{topAlert.description}</p>
      <p className="text-[10px] opacity-60">Source: {topAlert.source || 'openFDA'} — Non-blocking: pharmacist may proceed</p>
      </button>
      <button type="button" onClick={onDismiss} aria-label="Dismiss interaction alert" className="absolute right-2 top-2 opacity-50 hover:opacity-100 p-1" data-testid={`${testIdPrefix}-interaction-dismiss`}><X className="h-3.5 w-3.5" /></button>
      {rest.length > 0 && (
        <>
          <button type="button" onClick={() => setExpanded(v => !v)}
            className="flex items-center gap-1 text-[10px] font-semibold opacity-70 hover:opacity-100"
            data-testid={`${testIdPrefix}-interaction-expand`}>
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? 'Show less' : `+${rest.length} more`}
          </button>
          {expanded && rest.map((a) => (
            <div key={a.id} className="border-t border-current/20 pt-1.5 space-y-0.5">
              <p className="text-[11px] font-medium">{(a.drug_pair || []).join(' + ')}</p>
              <p className="text-[11px] opacity-80 line-clamp-2">{a.description}</p>
            </div>
          ))}
        </>
      )}
    </div>
    <POSInteractionReport open={reportOpen} onOpenChange={setReportOpen} alerts={alerts} triggerRef={triggerRef} testIdPrefix={testIdPrefix} />
    </>
  );
};

// ── Food & OTC Counseling Banner (dismissible, emerald) ────────────────────
const FoodOtcBanner = ({ alerts, dismissed, onDismiss, patientId, cartNames }) => {
  const [expanded, setExpanded] = useState(false);
  if (!alerts?.length || dismissed) return null;

  const topAlert = alerts[0];
  const rest = alerts.slice(1);
  const style = FOOD_SEVERITY_STYLE[topAlert.severity] || FOOD_SEVERITY_STYLE.info;
  const pairLabel = (topAlert.drug_pair || []).join(' ↔ ');

  return (
    <div className={`mx-4 mt-3 rounded-xl border px-3 py-2.5 space-y-1.5 ${style}`} data-testid="pos-food-otc-banner">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 font-semibold text-xs">
          <Apple className="h-3.5 w-3.5 shrink-0" />
          Counseling point{alerts.length > 1 ? `s (${alerts.length})` : ''}
          {topAlert.severity === 'critical' && <span className="ml-1 capitalize text-[10px] font-bold opacity-80">[critical]</span>}
        </div>
        <button type="button" onClick={onDismiss} className="opacity-50 hover:opacity-100 shrink-0" data-testid="pos-food-otc-dismiss">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {pairLabel && <p className="text-[11px] font-medium">{pairLabel}</p>}
      <p className="text-[11px] opacity-90 leading-snug line-clamp-2">{topAlert.description}</p>
      <p className="text-[10px] opacity-60">{topAlert.type === 'otc' ? 'OTC combination risk' : 'Drug-food interaction'} — advise the patient before dispensing</p>
      {rest.length > 0 && (
        <>
          <button type="button" onClick={() => setExpanded(v => !v)}
            className="flex items-center gap-1 text-[10px] font-semibold opacity-70 hover:opacity-100"
            data-testid="pos-food-otc-expand">
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? 'Show less' : `+${rest.length} more`}
          </button>
          {expanded && rest.map((a) => (
            <div key={a.id} className="border-t border-current/20 pt-1.5 space-y-0.5">
              <p className="text-[11px] font-medium">{(a.drug_pair || []).join(' ↔ ')}</p>
              <p className="text-[11px] opacity-80 line-clamp-2">{a.description}</p>
            </div>
          ))}
        </>
      )}
      <CounselingSlipActions alerts={alerts} patientId={patientId} cartNames={cartNames} testIdPrefix="pos-food-otc" />
    </div>
  );
};

export const POSCartPanel = ({
  cart, subtotal, total, prescriptionInfo, prescriptionNotes,
  expiryWarnings, refillWarnings, selectedPatientId,
  interactionAlerts, allergyAlerts, foodAlerts, interactionDismissed, foodAlertsDismissed,
  onDismissInteractions, onDismissFoodAlerts,
  onUpdateQuantity, onQuantityInput, onQuantityBlur, onRemove, onSwap,
  onHold, onCheckout,
}) => (
  <Card className="flex flex-col h-full min-h-0 overflow-hidden">
    <CardHeader className="pb-3 px-4 pt-4 shrink-0">
      <CardTitle className="text-base font-semibold flex items-center gap-2">
        <ShoppingCart className="h-4 w-4" /> Cart
        {cart.length > 0 && (
          <Badge variant="secondary" className="text-xs ml-auto">{cart.length} items</Badge>
        )}
        {allergyAlerts?.length > 0 && (
          <Badge className="text-[10px] h-5 px-1.5 gap-0.5 bg-rose-600 hover:bg-rose-600" data-testid="pos-allergy-count-badge">
            <ShieldAlert className="h-2.5 w-2.5" />{allergyAlerts.length} allergy
          </Badge>
        )}
        {interactionAlerts?.length > 0 && !interactionDismissed && (
          <Badge variant="destructive" className="text-[10px] h-5 px-1.5 gap-0.5" data-testid="pos-interaction-count-badge">
            <Zap className="h-2.5 w-2.5" />{interactionAlerts.length}
          </Badge>
        )}
        {foodAlerts?.length > 0 && !foodAlertsDismissed && (
          <Badge className="text-[10px] h-5 px-1.5 gap-0.5 bg-emerald-600 hover:bg-emerald-600" data-testid="pos-food-otc-count-badge">
            <Apple className="h-2.5 w-2.5" />{foodAlerts.length}
          </Badge>
        )}
      </CardTitle>
      {prescriptionInfo && (
        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
          <FileText className="h-3 w-3 text-primary" />
          <span>From Prescription</span>
          {prescriptionInfo.doctorName && <span>• Dr. {prescriptionInfo.doctorName}</span>}
        </div>
      )}
    </CardHeader>
    <Separator />
    <ScrollArea className="flex-1 min-h-0 px-4" data-testid="pos-cart-scroll-area">
    {prescriptionNotes && (
      <Alert className="mt-3 border-amber-500/50 bg-amber-500/10" data-testid="prescription-warning">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-xs text-amber-700 dark:text-amber-400">
          {prescriptionNotes.split('\n')[0]}
        </AlertDescription>
      </Alert>
    )}
    {/* Allergy alerts — always shown, non-dismissible */}
    <AllergyBanner alerts={allergyAlerts} />
    {/* Interaction alerts — dismissible */}
    <InteractionBanner
      alerts={interactionAlerts}
      dismissed={interactionDismissed}
      onDismiss={onDismissInteractions}
    />
    {/* Food/OTC counseling points — dismissible */}
    <FoodOtcBanner
      alerts={foodAlerts}
      dismissed={foodAlertsDismissed}
      onDismiss={onDismissFoodAlerts}
      patientId={selectedPatientId}
      cartNames={cart.map((i) => i.generic_name || i.product_name || '').filter(Boolean)}
    />
      {cart.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground" data-testid="cart-empty-state">
          <ShoppingCart className="h-8 w-8 mb-2 opacity-30" />
          <p className="text-sm">Cart is empty</p>
          <p className="text-xs mt-1">Click products to add them</p>
        </div>
      ) : (
        <div className="space-y-3 py-3">
          {cart.map((item) => (
            <POSCartItem
              key={item.product_id}
              item={item}
              expiryWarning={expiryWarnings[item.product_id]}
              refillWarning={refillWarnings[item.product_id]}
              patientId={selectedPatientId}
              onUpdateQuantity={onUpdateQuantity}
              onQuantityInput={onQuantityInput}
              onQuantityBlur={onQuantityBlur}
              onRemove={onRemove}
              onSwap={(target) => onSwap(item.product_id, target)}
            />
          ))}
        </div>
      )}
    </ScrollArea>
      <div className="border-t border-border bg-card p-4 space-y-3 shrink-0" data-testid="pos-cart-footer">
        <div className="flex justify-between text-sm" data-testid="pos-cart-subtotal">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="font-medium">{fmtCurrency(subtotal)}</span>
        </div>
        <div className="flex gap-2" data-testid="pos-cart-actions">
          <Button
            variant="outline"
            className="h-11 px-3 gap-1.5"
            onClick={onHold}
            disabled={cart.length === 0}
            title="Hold this transaction and serve another customer"
            data-testid="hold-sale-btn"
          >
            <Pause className="h-4 w-4" /> Hold
          </Button>
          <Button className="flex-1 min-w-0 h-11 text-sm font-semibold gap-2" onClick={onCheckout} disabled={cart.length === 0} data-testid="checkout-btn">
            <Receipt className="h-4 w-4" /> Checkout ({fmtCurrency(total)})
          </Button>
        </div>
      </div>
  </Card>
);
